import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { stackedPair } from '../kits/stackKit';
import { estimateMeasure, translateSpecs } from '../kits/specs';
import type { PrimitiveSpec, TextMeasurer, TextSpec } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Mixed number — a whole number set beside a stacked fraction (CATALOG M1a),
 * e.g. 2 3/4. Built on stackKit.stackedPair (the FRACTION BAR LAW: the bar
 * re-derives to max(numerator, denominator) width on every edit), with the
 * whole number composed to its LEFT via translateSpecs.
 *
 * THE TYPESETTING — why the sizes and the vertical placement.
 *   • The whole number is set LARGER than the fraction's digits (the LaTeX
 *     `2\frac34` relationship: the whole is body-size, the fraction is a
 *     ~0.66x "script" size). A same-size whole reads as a subscript; an
 *     oversized one dwarfs the fraction. WHOLE_SIZE / FRACTION_SIZE ≈ 1.5 is
 *     the textbook balance.
 *   • The whole is VERTICALLY CENTERED ON THE BAR. In a stackedPair the bar's
 *     midline (barY) is also the exact vertical center of the whole fraction
 *     block (numerator box and denominator box are symmetric about it), so
 *     "centered on the bar" == "centered on the fraction". Digits carry no
 *     descender, so their optical center sits a hair above the em-box center;
 *     a small downward nudge (DIGIT_OPTICAL_CENTER) drops the digit's ink
 *     center onto barY rather than the box center.
 *
 * All THREE numbers (whole, numerator, denominator) are param-tagged, so each
 * is double-click editable on the poster and regenerates in place.
 *
 * Emits FabricText + Line only, grouped and tagged {tool,version,params} —
 * plain fabric JSON, vector at any export dpi.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the fraction.ts/handwriting.ts pattern) — params
// arrive untrusted (user-typed or replayed from stored JSON); generate() must
// never throw.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value here (= blank slot). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Text measurement — canvas TextMetrics when available (insert and Apply both
// ensureFont the family first), estimate fallback elsewhere (SSR/node safety).
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

/** Ink + face defaults shared with the other math manipulatives (math.ts). */
const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';

/** The stacked fraction's digit size; the whole number is ~1.5x this. */
const FRACTION_SIZE = 100;
const WHOLE_SIZE = 150;

/**
 * Downward nudge (in ems of WHOLE_SIZE) applied to the box-centered whole
 * number so its DIGIT ink centers on the bar. Fabric's alphabetic baseline is
 * 0.879em from the box top; a cap/figure height of ~0.70em puts the digit's
 * optical center at ~0.529em, versus the box center at 0.565em — the digit
 * center rides ~0.036em above the box center, so nudge the box down by that.
 */
const DIGIT_OPTICAL_CENTER = 0.04;

const mixedNumber: ToolRecipe = {
  id: 'mixed-number',
  domain: 'math',
  tags: ['fractions', 'mixed-number', 'notation', 'math-notation'],
  name: 'Mixed number',
  category: 'math',
  version: 1,
  description:
    'A whole number beside a stacked fraction, like 2 3/4. The bar sizes itself to the widest number; double-click any number on the poster to change it.',
  defaults: {
    whole: '2',
    numerator: '3',
    denominator: '4',
    barWeight: 8,
    color: INK,
    fontFamily: DEFAULT_FONT,
  },
  schema: [
    {
      key: 'whole',
      label: 'Whole number',
      type: 'text',
      help: 'The number to the left of the fraction. Leave empty for a plain fraction.',
    },
    {
      key: 'numerator',
      label: 'Top number',
      type: 'text',
      help: 'The numerator. Leave empty for a fill-in slot.',
    },
    {
      key: 'denominator',
      label: 'Bottom number',
      type: 'text',
      help: 'The denominator. Leave empty for a fill-in slot.',
    },
    {
      key: 'barWeight',
      label: 'Bar thickness',
      type: 'number',
      min: 2,
      max: 30,
      step: 1,
      section: 'Style',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
  ],
  async generate(params) {
    const whole = rawStr(params.whole, '2');
    const numerator = rawStr(params.numerator, '3');
    const denominator = rawStr(params.denominator, '4');
    const barWeight = clamp(num(params.barWeight, 8), 1, 60);
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const measure = makeMeasurer(family);

    // The stacked fraction — numerator over the self-sizing bar over the
    // denominator; both slots carry their dblclick param tags.
    const frac = stackedPair({
      numerator,
      denominator,
      fontSize: FRACTION_SIZE,
      barWeight,
      color,
      fontFamily: family,
      measure,
      numeratorParam: 'numerator',
      denominatorParam: 'denominator',
    });

    // Whole number: sits to the left, its digit vertically centered on the bar.
    const wholeW = whole ? measure(whole, WHOLE_SIZE) : 0;
    const gap = whole ? FRACTION_SIZE * 0.16 : 0;
    const wholeSpec: TextSpec = {
      kind: 'text',
      text: whole,
      x: 0,
      y: frac.barY + WHOLE_SIZE * DIGIT_OPTICAL_CENTER,
      fontSize: WHOLE_SIZE,
      fontFamily: family,
      fill: color,
      originX: 'left',
      originY: 'center',
      param: 'whole',
    };

    // Shift the fraction block clear of the whole number.
    const fracSpecs = translateSpecs(frac.specs, wholeW + gap, 0);
    const specs: PrimitiveSpec[] = [wholeSpec, ...fracSpecs];

    // Children carry absolute coords; Group derives the bbox and the caller
    // places it (mirrors every other recipe).
    const group = new Group(assembleSpecs(specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, mixedNumber, params);
  },
};

registerRecipe(mixedNumber);
